import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	type AgentStatus,
	type ArtemisAgentFilterInput,
	type ArtemisAgentSortInput,
	CreateAgentDocument,
	type CreateAgentInput,
	DeleteAgentDocument,
	GetAgentDeploymentOptionsDocument,
	GetAgentDocument,
	GetAgentsDocument,
	PingAgentDocument,
	RotateAgentCredentialDocument,
	SetAgentRuntimeVersionDocument,
	UpdateAgentDocument,
	type UpdateAgentInput,
} from "@/common/artemis-sdk/graphql";
// import { gqlFetch } from "@/common/graphql-client";
import type { Agent } from "../model";
import { useGqlClient } from "../use-gql-client";
import { buildWhere, toOrder, usePagedList } from "./use-paged-list";

/**
 * Fetches the first page of agents for a workspace (array form, for lookups/dropdowns).
 * For the paginated list use {@link useAgentsPaged}.
 */
export function useAgents(workspaceId: string) {
	const gqlFetch = useGqlClient();
	return useQuery({
		queryKey: ["agents", workspaceId],
		queryFn: async () => {
			const data = await gqlFetch(GetAgentsDocument, { workspaceId });
			return (data.agents?.nodes as Agent[]) ?? [];
		},
		enabled: !!workspaceId,
	});
}

/**
 * Fetches the deploy-time options (supported regions) for remote agent deployment.
 * The list is static config, so it's cached indefinitely.
 */
export function useDeploymentOptions() {
	const gqlFetch = useGqlClient();
	return useQuery({
		queryKey: ["deploymentOptions"],
		queryFn: async () => {
			const data = await gqlFetch(GetAgentDeploymentOptionsDocument);
			return data.agentDeploymentOptions;
		},
		staleTime: Number.POSITIVE_INFINITY,
	});
}

/**
 * Server-side paginated agent list for a workspace: Next/Prev cursor pagination plus
 * server-side search (name/endpointUrl/id), status filter, and sort.
 */
export function useAgentsPaged(workspaceId: string) {
	const gqlFetch = useGqlClient();
	return usePagedList<Agent>({
		baseKey: ["agents", workspaceId],
		enabled: !!workspaceId,
		initialSort: [{ id: "createdAt", desc: true }],
		fetchPage: async ({ debouncedSearch, filters, sort, window }) => {
			const data = await gqlFetch(GetAgentsDocument, {
				workspaceId,
				...window,
				order: toOrder<ArtemisAgentSortInput>(sort),
				where: buildWhere(
					debouncedSearch,
					["name", "endpointUrl", "id"],
					filters,
				) as ArtemisAgentFilterInput | undefined,
			});
			const c = data.agents;
			return {
				nodes: (c?.nodes as Agent[]) ?? [],
				totalCount: c?.totalCount ?? 0,
				pageInfo: c?.pageInfo ?? { hasNextPage: false, hasPreviousPage: false },
			};
		},
	});
}

/**
 * Fetches a single agent by its ID, scoped to the given workspace (ADR-028).
 */
export function useAgent(agentId: string, workspaceId: string) {
	const gqlFetch = useGqlClient();
	return useQuery({
		queryKey: ["agent", agentId, workspaceId],
		queryFn: async () => {
			const data = await gqlFetch(GetAgentDocument, { agentId, workspaceId });
			return data.agent as Agent;
		},
		enabled: !!agentId && !!workspaceId,
	});
}

/**
 * Creates (registers) a new agent. The payload's `credentials` field is the ONE-TIME plaintext
 * credential reveal for External agents (ADR-050) — present only on the original creation response,
 * never retrievable again. The caller must show it to the user before dropping it.
 */
export function useCreateAgent() {
	const gqlFetch = useGqlClient();
	const { mutate } = useMutation({
		mutationFn: async (input: CreateAgentInput) => {
			const data = await gqlFetch(CreateAgentDocument, { input });
			return data.createAgent;
		},
	});
	return mutate;
}

/**
 * Rotates an External agent's client secret (ADR-050): new secret under the same client id, optional
 * expiry; the old secret stops working immediately. The response carries the one-time reveal of
 * the new secret. Invalidates the agent query so the new expiry shows.
 */
export function useRotateAgentCredential(agentId: string, workspaceId: string) {
	const queryClient = useQueryClient();
	const gqlFetch = useGqlClient();
	const { mutate, isPending } = useMutation({
		mutationFn: async (clientSecretExpiresAt?: string) => {
			const data = await gqlFetch(RotateAgentCredentialDocument, {
				agentId,
				workspaceId,
				clientSecretExpiresAt,
			});
			return data.rotateAgentCredential;
		},
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: ["agent", agentId, workspaceId],
			}),
	});
	return { rotateAgentCredential: mutate, isRotating: isPending };
}

/** Updates an existing agent. */
export function useUpdateAgent(agentId: string, workspaceId: string) {
	const queryClient = useQueryClient();
	const gqlFetch = useGqlClient();
	const { mutate } = useMutation({
		mutationFn: async (input: UpdateAgentInput) => {
			const data = await gqlFetch(UpdateAgentDocument, {
				agentId,
				workspaceId,
				input,
			});
			return data.updateAgent.agent;
		},
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: ["agent", agentId, workspaceId],
			}),
	});
	return mutate;
}

/**
 * Permanently deletes an agent. Pass `force: true` to remove the record even when the remote runtime
 * cannot be torn down (e.g. the customer deleted it in their own account); the caller then owns
 * deleting the leftover cloud resource.
 */
export function useDeleteAgent(agentId: string, workspaceId: string) {
	const gqlFetch = useGqlClient();
	const { mutate } = useMutation({
		mutationFn: async (force?: boolean) => {
			const data = await gqlFetch(DeleteAgentDocument, {
				agentId,
				workspaceId,
				force,
			});
			return data.deleteAgent.success;
		},
	});
	return mutate;
}

/**
 * Sets an agent's customer-controlled status (Enabled/Disabled) via updateAgent. Reachability
 * (health) is unaffected — that's system-observed. Invalidates the agent and agent-list queries.
 */
export function useSetAgentStatus(agentId: string, workspaceId: string) {
	const queryClient = useQueryClient();
	const gqlFetch = useGqlClient();
	const { mutate, isPending } = useMutation({
		mutationFn: async (status: AgentStatus) => {
			const data = await gqlFetch(UpdateAgentDocument, {
				agentId,
				workspaceId,
				input: { status },
			});
			return data.updateAgent.agent;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["agent", agentId, workspaceId],
			});
			queryClient.invalidateQueries({ queryKey: ["agents", workspaceId] });
		},
	});
	return { setAgentStatus: mutate, isSettingStatus: isPending };
}

/**
 * Switches a deployed agent to a different runtime version, updating its live AgentCore runtime in
 * place. Invalidates the agent and agent-list queries so the new version + health show immediately.
 */
export function useSetAgentRuntimeVersion(
	agentId: string,
	workspaceId: string,
) {
	const queryClient = useQueryClient();
	const gqlFetch = useGqlClient();
	const { mutate, isPending } = useMutation({
		mutationFn: async (version: string) => {
			const data = await gqlFetch(SetAgentRuntimeVersionDocument, {
				agentId,
				workspaceId,
				version,
			});
			return data.setAgentRuntimeVersion.agent;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["agent", agentId, workspaceId],
			});
			queryClient.invalidateQueries({ queryKey: ["agents", workspaceId] });
		},
	});
	return { setAgentRuntimeVersion: mutate, isSettingRuntimeVersion: isPending };
}

/** Pings an agent's A2A endpoint to check whether it's online. */
export function usePingAgent(agentId: string, workspaceId: string) {
	const gqlFetch = useGqlClient();
	const { mutate, isPending } = useMutation({
		mutationFn: async () => {
			const data = await gqlFetch(PingAgentDocument, { agentId, workspaceId });
			return data.pingAgent.health;
		},
	});
	return { pingAgent: mutate, isPinging: isPending };
}
